import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    CircularProgress,
    Paper,
    alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TimelineIcon from '@mui/icons-material/Timeline';
import AddCommentIcon from '@mui/icons-material/AddComment';
import { LpsDevice, LpsCustodyEvent, DeviceStatus } from '../lps.types';
import { getDeviceCustodyHistory, updateDevice, addDeviceNote } from '../lps.api';

interface Props {
    open: boolean;
    device: LpsDevice | null;
    onClose: () => void;
    onSave: () => void;
}

const STATUS_COLORS: Record<DeviceStatus, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
    in_stock: 'success',
    deployed: 'info',
    maintenance: 'warning',
    defective: 'error',
    returned: 'default',
    decommissioned: 'default',
};

const TYPE_LABELS: Record<string, string> = {
    anchor: 'Anchor',
    tracker: 'Tracker',
    gateway: 'Gateway',
};

const STATUS_OPTIONS: DeviceStatus[] = [
    'in_stock',
    'deployed',
    'maintenance',
    'defective',
    'returned',
    'decommissioned',
];

const formatStatus = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Replace raw installation UUID in stored notes with the resolved installation name when available. */
const custodyNotesDisplay = (event: LpsCustodyEvent): string => {
    const raw = event.notes?.trim() || '';
    if (!raw) {
        return '';
    }
    if (event.installation_uuid && event.installation_name) {
        return raw.split(event.installation_uuid).join(event.installation_name);
    }
    return raw;
};

const formatDate = (d: string | null) =>
    d
        ? new Date(d).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '--';

const LpsDeviceDetailDialog: React.FC<Props> = ({ open, device, onClose, onSave }) => {
    const [custodyEvents, setCustodyEvents] = useState<LpsCustodyEvent[]>([]);
    const [loading, setLoading] = useState(false);
    const [editStatus, setEditStatus] = useState<DeviceStatus | ''>('');
    const [eventNotes, setEventNotes] = useState('');
    const [noteText, setNoteText] = useState('');
    const [saving, setSaving] = useState(false);
    const [showAddNote, setShowAddNote] = useState(false);

    useEffect(() => {
        if (open && device) {
            setEditStatus(device.status);
            setEventNotes('');
            setNoteText('');
            setShowAddNote(false);
            fetchCustodyHistory(device.uuid);
        }
    }, [open, device]);

    const fetchCustodyHistory = async (uuid: string) => {
        setLoading(true);
        try {
            const response = await getDeviceCustodyHistory(uuid);
            // Resource collection may return { data: [...] } or a plain array
            const events = Array.isArray(response.data) ? response.data : response.data?.data ?? [];
            setCustodyEvents(events);
        } catch (err) {
            console.error('Failed to fetch custody history', err);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async () => {
        if (!device || !editStatus || editStatus === device.status) return;
        setSaving(true);
        try {
            await updateDevice(device.uuid, {
                status: editStatus,
                event_notes: eventNotes || undefined,
            });
            onSave();
        } catch (err: any) {
            console.error('Failed to update device', err);
        } finally {
            setSaving(false);
        }
    };

    const handleAddNote = async () => {
        if (!device || !noteText.trim()) return;
        setSaving(true);
        try {
            await addDeviceNote(device.uuid, noteText);
            setNoteText('');
            setShowAddNote(false);
            fetchCustodyHistory(device.uuid);
        } catch (err) {
            console.error('Failed to add note', err);
        } finally {
            setSaving(false);
        }
    };

    if (!device) return null;

    const formatCost = (cents: number | null) =>
        cents != null ? `$${(cents / 100).toFixed(2)}` : '--';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {device.serial_number}
                    </Typography>
                    <Chip
                        label={TYPE_LABELS[device.device_type] || device.device_type}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                    />
                    <Chip
                        label={formatStatus(device.status)}
                        size="small"
                        color={STATUS_COLORS[device.status]}
                        sx={{ fontWeight: 500 }}
                    />
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {/* Device Info Grid */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <InfoItem label="Model" value={device.model || '--'} />
                    <InfoItem label="Unit Cost" value={formatCost(device.unit_cost_cents)} />
                    <InfoItem label="Received" value={formatDate(device.received_at)} />
                    <InfoItem label="Deployed" value={formatDate(device.deployed_at)} />
                    <InfoItem label="Decommissioned" value={formatDate(device.decommissioned_at)} />
                    <InfoItem label="Notes" value={device.notes || '--'} />
                    {device.status === 'deployed' && (
                        <InfoItem label="Installation" value={device.installation_name || '--'} />
                    )}
                </Box>

                {/* Status Update */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Update Status
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={editStatus}
                                onChange={(e) => setEditStatus(e.target.value as DeviceStatus)}
                                label="Status"
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <MenuItem key={s} value={s}>
                                        {formatStatus(s)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Event Notes"
                            value={eventNotes}
                            onChange={(e) => setEventNotes(e.target.value)}
                            sx={{ flex: 1 }}
                            placeholder="Reason for status change..."
                        />
                        <Button
                            variant="contained"
                            onClick={handleStatusUpdate}
                            disabled={saving || editStatus === device.status || !editStatus}
                            size="medium"
                        >
                            {saving ? <CircularProgress size={20} /> : 'Update'}
                        </Button>
                    </Box>
                </Paper>

                {/* Custody Timeline */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimelineIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Chain of Custody
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        startIcon={<AddCommentIcon />}
                        onClick={() => setShowAddNote(!showAddNote)}
                        variant="outlined"
                    >
                        Add Note
                    </Button>
                </Box>

                {showAddNote && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Add a note to this device's history..."
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                        />
                        <Button
                            variant="contained"
                            onClick={handleAddNote}
                            disabled={saving || !noteText.trim()}
                            size="small"
                        >
                            Save
                        </Button>
                    </Box>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Box sx={{ position: 'relative', pl: 3 }}>
                        {/* Timeline line */}
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 10,
                                top: 0,
                                bottom: 0,
                                width: 2,
                                bgcolor: 'divider',
                            }}
                        />
                        {custodyEvents.map((event, idx) => {
                            const noteText = custodyNotesDisplay(event);
                            return (
                                <Box key={event.id} sx={{ position: 'relative', mb: 2 }}>
                                    {/* Timeline dot */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: -21,
                                            top: 6,
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor: idx === 0 ? 'primary.main' : 'grey.400',
                                            border: '2px solid',
                                            borderColor: 'background.paper',
                                        }}
                                    />
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            bgcolor:
                                                idx === 0
                                                    ? (theme) =>
                                                          alpha(theme.palette.primary.main, 0.04)
                                                    : 'transparent',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mb: 0.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Chip
                                                    label={formatStatus(event.event_type)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem', height: 22 }}
                                                />
                                                {event.from_status &&
                                                    event.to_status &&
                                                    event.from_status !== event.to_status && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {formatStatus(event.from_status)} &rarr;{' '}
                                                            {formatStatus(event.to_status)}
                                                        </Typography>
                                                    )}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(event.created_at)}
                                            </Typography>
                                        </Box>
                                        {(event.performed_by_name || event.performed_by_uuid) && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block', mt: 0.25 }}
                                            >
                                                {event.performed_by_name
                                                    ? `Performed by ${event.performed_by_name}`
                                                    : 'Performed by (name unavailable)'}
                                            </Typography>
                                        )}
                                        {noteText && (
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 0.5, color: 'text.primary' }}
                                            >
                                                {noteText}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Box>
                            );
                        })}
                        {custodyEvents.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                No custody events recorded.
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const InfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value}
        </Typography>
    </Box>
);

export default LpsDeviceDetailDialog;
